import React, { memo } from 'react';
import { Card, Row, Col, Badge, Tooltip, Spin } from 'antd';
import { 
  CheckCircleOutlined, 
  ExclamationCircleOutlined, 
  CloseCircleOutlined,
  QuestionCircleOutlined,
  ClockCircleOutlined,
  DatabaseOutlined,
  CloudOutlined,
  ApiOutlined,
  LoadingOutlined
} from '@ant-design/icons';
import { formatDuration, getStatusColor, getStatusIcon } from '../../config/performanceDashboard';
import './PerformanceDashboard.css';

const HealthStatus = memo(({ 
  healthData, 
  loading = false, 
  className = '',
  style = {} 
}) => {
  if (loading) {
    return (
      <Card 
        title="Health Status" 
        className={`health-status ${className}`}
        style={style}
      >
        <div className="health-status-loading">
          <Spin 
            indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} 
            size="large"
          />
        </div>
      </Card>
    );
  }

  if (!healthData) {
    return (
      <Card 
        title="Health Status" 
        className={`health-status ${className}`}
        style={style}
      >
        <div className="health-status-no-data">
          <QuestionCircleOutlined style={{ fontSize: 48, color: '#8c8c8c' }} />
          <p>No health data available</p>
        </div>
      </Card>
    );
  }

  const { services, uptime, lastCheck } = healthData;

  // Service status component
  const ServiceStatus = ({ service, name, icon }) => {
    const status = service?.status || 'unknown';
    const responseTime = service?.responseTime || 0;
    const lastCheck = service?.lastCheck;
    
    const statusColor = getStatusColor(status);
    const statusIconType = getStatusIcon(status);
    
    const renderStatusIcon = () => {
      switch (statusIconType) {
        case 'check-circle':
          return <CheckCircleOutlined style={{ color: statusColor }} />;
        case 'exclamation-circle':
          return <ExclamationCircleOutlined style={{ color: statusColor }} />;
        case 'close-circle':
          return <CloseCircleOutlined style={{ color: statusColor }} />;
        default:
          return <QuestionCircleOutlined style={{ color: statusColor }} />;
      }
    };

    return (
      <div className="service-status-item">
        <div className="service-status-header">
          <div className="service-status-name">
            <span className="service-status-icon">{icon}</span>
            <span className="service-status-title">{name}</span>
          </div>
          <div className="service-status-indicator">
            {renderStatusIcon()}
          </div>
        </div>
        
        <div className="service-status-details">
          <div className="service-status-response-time">
            <span className="service-status-label">Response Time:</span>
            <span className="service-status-value">
              {formatDuration(responseTime)}
            </span>
          </div>
          
          {lastCheck && (
            <div className="service-status-last-check">
              <ClockCircleOutlined style={{ marginRight: 4 }} />
              <span className="service-status-label">Last Check:</span>
              <span className="service-status-value">
                {new Date(lastCheck).toLocaleTimeString()}
              </span>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Overall health status
  const getOverallStatus = () => {
    if (!services) return 'unknown';
    
    const serviceStatuses = Object.values(services).map(service => service?.status);
    
    if (serviceStatuses.every(status => status === 'healthy')) {
      return 'healthy';
    } else if (serviceStatuses.some(status => status === 'critical')) {
      return 'critical';
    } else if (serviceStatuses.some(status => status === 'warning')) {
      return 'warning';
    } else {
      return 'unknown';
    }
  };

  const overallStatus = getOverallStatus();
  const overallStatusColor = getStatusColor(overallStatus);

  return (
    <Card 
      title={
        <div className="health-status-title">
          <span>Health Status</span>
          <Badge 
            color={overallStatusColor} 
            text={overallStatus.toUpperCase()}
            style={{ marginLeft: 8 }}
          />
        </div>
      }
      className={`health-status ${className}`}
      style={style}
    >
      <div className="health-status-content">
        {/* Services Status */}
        <div className="health-status-services">
          <h4>Services</h4>
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={8}>
              <ServiceStatus
                service={services?.database}
                name="Database"
                icon={<DatabaseOutlined />}
              />
            </Col>
            <Col xs={24} sm={12} md={8}>
              <ServiceStatus
                service={services?.redis}
                name="Redis"
                icon={<CloudOutlined />}
              />
            </Col>
            <Col xs={24} sm={12} md={8}>
              <ServiceStatus
                service={services?.api}
                name="API"
                icon={<ApiOutlined />}
              />
            </Col>
          </Row>
        </div>

        {/* System Information */}
        <div className="health-status-system">
          <h4>System Information</h4>
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12}>
              <div className="health-status-info-item">
                <span className="health-status-info-label">Uptime:</span>
                <span className="health-status-info-value">
                  {uptime ? formatDuration(uptime * 1000) : 'N/A'}
                </span>
              </div>
            </Col>
            <Col xs={24} sm={12}>
              <div className="health-status-info-item">
                <span className="health-status-info-label">Last Check:</span>
                <span className="health-status-info-value">
                  {lastCheck ? new Date(lastCheck).toLocaleString() : 'N/A'}
                </span>
              </div>
            </Col>
          </Row>
        </div>

        {/* Overall Status Summary */}
        <div className="health-status-summary">
          <div 
            className="health-status-summary-card"
            style={{ borderLeftColor: overallStatusColor }}
          >
            <div className="health-status-summary-content">
              <div className="health-status-summary-icon">
                {overallStatus === 'healthy' && <CheckCircleOutlined style={{ color: overallStatusColor }} />}
                {overallStatus === 'warning' && <ExclamationCircleOutlined style={{ color: overallStatusColor }} />}
                {overallStatus === 'critical' && <CloseCircleOutlined style={{ color: overallStatusColor }} />}
                {overallStatus === 'unknown' && <QuestionCircleOutlined style={{ color: overallStatusColor }} />}
              </div>
              <div className="health-status-summary-text">
                <div className="health-status-summary-title">
                  Overall Status: {overallStatus.toUpperCase()}
                </div>
                <div className="health-status-summary-description">
                  {overallStatus === 'healthy' && 'All services are running normally'}
                  {overallStatus === 'warning' && 'Some services may have issues'}
                  {overallStatus === 'critical' && 'Critical issues detected'}
                  {overallStatus === 'unknown' && 'Unable to determine status'}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
});

HealthStatus.displayName = 'HealthStatus';

export default HealthStatus;
